import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Paper,
  Group,
  Text,
  Button,
  Table,
  Badge,
  ActionIcon,
  Tooltip,
  TextInput,
  Loader,
  Pagination,
  Menu,
  Alert,
  Modal,
  useMantineColorScheme
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import {
  IconUpload,
  IconSearch,
  IconTrash,
  IconRefresh,
  IconCheck,
  IconDotsVertical,
  IconEye,
  IconFileTypePdf,
  IconFileTypeDocx,
  IconFileTypeXls,
  IconFileText,
  IconAlertTriangle,
  IconClock,
  IconInfoCircle
} from '@tabler/icons-react';
import { aiKnowledgeApi, AIKnowledgeDocument } from '@/api/aiKnowledge.api';
import UploadDocumentModal from './UploadDocumentModal';

interface DocumentsTabProps {
  onDataChange: () => void;
}

const DocumentsTab = ({ onDataChange }: DocumentsTabProps) => {
  const { t } = useTranslation();
  const { colorScheme } = useMantineColorScheme();
  const [documents, setDocuments] = useState<AIKnowledgeDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 400);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailDoc, setDetailDoc] = useState<AIKnowledgeDocument | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await aiKnowledgeApi.getDocuments({
        page,
        limit: 15,
        search: debouncedSearch || undefined
      });
      if (response.data.success) {
        setDocuments(response.data.data.items);
        setTotalPages(response.data.data.pagination.totalPages);
      }
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      notifications.show({
        title: t('common.error'),
        message: t('aiKnowledge.errors.loadFailed'),
        color: 'red'
      });
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleDelete = (doc: AIKnowledgeDocument) => {
    modals.openConfirmModal({
      title: t('aiKnowledge.documents.deleteTitle'),
      children: (
        <Text size="sm">
          {t('aiKnowledge.documents.deleteConfirm', { name: doc.title })}
        </Text>
      ),
      labels: {
        confirm: t('common.delete'),
        cancel: t('common.cancel')
      },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await aiKnowledgeApi.deleteDocument(doc.id);
          notifications.show({
            title: t('common.success'),
            message: t('aiKnowledge.documents.deleted'),
            color: 'green',
            icon: <IconCheck size={18} />
          });
          fetchDocuments();
          onDataChange();
        } catch (error) {
          notifications.show({
            title: t('common.error'),
            message: t('aiKnowledge.errors.deleteFailed'),
            color: 'red'
          });
        }
      }
    });
  };

  const handleReindex = async (doc: AIKnowledgeDocument) => {
    try {
      await aiKnowledgeApi.reindexDocument(doc.id);
      notifications.show({
        title: t('common.success'),
        message: t('aiKnowledge.documents.reindexStarted'),
        color: 'green',
        icon: <IconCheck size={18} />
      });
      setTimeout(fetchDocuments, 2000);
    } catch (error) {
      notifications.show({
        title: t('common.error'),
        message: t('aiKnowledge.errors.reindexFailed'),
        color: 'red'
      });
    }
  };

  const handleViewDetail = async (doc: AIKnowledgeDocument) => {
    setDetailLoading(true);
    setDetailModalOpen(true);
    try {
      const response = await aiKnowledgeApi.getDocument(doc.id);
      if (response.data.success) {
        setDetailDoc(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load document detail:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'pdf': return <IconFileTypePdf size={20} color="var(--mantine-color-red-5)" />;
      case 'docx': return <IconFileTypeDocx size={20} color="var(--mantine-color-blue-5)" />;
      case 'xlsx': return <IconFileTypeXls size={20} color="var(--mantine-color-green-5)" />;
      default: return <IconFileText size={20} color="var(--mantine-color-gray-5)" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'indexed':
        return <Badge color="green" variant="light" size="sm" leftSection={<IconCheck size={12} />}>{t('aiKnowledge.status.indexed')}</Badge>;
      case 'processing':
        return <Badge color="yellow" variant="light" size="sm" leftSection={<IconClock size={12} />}>{t('aiKnowledge.status.processing')}</Badge>;
      case 'error':
        return <Badge color="red" variant="light" size="sm" leftSection={<IconAlertTriangle size={12} />}>{t('aiKnowledge.status.error')}</Badge>;
      case 'pending':
        return <Badge color="gray" variant="light" size="sm" leftSection={<IconClock size={12} />}>{t('aiKnowledge.status.pending')}</Badge>;
      default:
        return <Badge variant="light" size="sm">{status}</Badge>;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <Stack gap="md">
      {/* Info */}
      <Alert variant="light" color="blue" icon={<IconInfoCircle size={18} />}>
        <Text size="sm">{t('aiKnowledge.documents.description')}</Text>
      </Alert>

      {/* Header */}
      <Paper p="md" radius="md" withBorder>
        <Group justify="space-between">
          <TextInput
            placeholder={t('aiKnowledge.documents.searchPlaceholder')}
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            w={300}
          />
          <Group gap="sm">
            <Button
              leftSection={<IconUpload size={16} />}
              onClick={() => setUploadModalOpen(true)}
              variant="gradient"
              gradient={{ from: 'blue', to: 'violet' }}
            >
              {t('aiKnowledge.documents.upload')}
            </Button>
          </Group>
        </Group>
      </Paper>

      {/* Table */}
      {loading ? (
        <Group justify="center" py="xl"><Loader /></Group>
      ) : documents.length === 0 ? (
        <Paper p="xl" radius="md" withBorder>
          <Stack align="center" gap="md">
            <IconFileText size={48} color="var(--mantine-color-dimmed)" />
            {search ? (
              <Text c="dimmed" ta="center">{t('aiKnowledge.documents.noSearchResults')}</Text>
            ) : (
              <>
                <Text c="dimmed" ta="center">{t('aiKnowledge.documents.noDocuments')}</Text>
                <Button
                  leftSection={<IconUpload size={16} />}
                  onClick={() => setUploadModalOpen(true)}
                  variant="light"
                >
                  {t('aiKnowledge.documents.uploadFirst')}
                </Button>
              </>
            )}
          </Stack>
        </Paper>
      ) : (
        <Paper radius="md" withBorder style={{ overflow: 'hidden' }}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('aiKnowledge.documents.columns.title')}</Table.Th>
                <Table.Th>{t('aiKnowledge.documents.columns.type')}</Table.Th>
                <Table.Th>{t('aiKnowledge.documents.columns.size')}</Table.Th>
                <Table.Th>
                  <Tooltip label={t('aiKnowledge.tooltips.chunksHelp')}>
                    <Group gap={4} style={{ cursor: 'help' }}>
                      <Text size="sm" fw={600}>{t('aiKnowledge.documents.columns.chunks')}</Text>
                      <IconInfoCircle size={14} color="var(--mantine-color-dimmed)" />
                    </Group>
                  </Tooltip>
                </Table.Th>
                <Table.Th>{t('aiKnowledge.documents.columns.status')}</Table.Th>
                <Table.Th>{t('aiKnowledge.documents.columns.createdAt')}</Table.Th>
                <Table.Th w={80}>{t('aiKnowledge.documents.columns.actions')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {documents.map((doc) => (
                <Table.Tr key={doc.id}>
                  <Table.Td>
                    <Group gap="xs">
                      {getFileIcon(doc.file_type)}
                      <div>
                        <Text size="sm" fw={500} lineClamp={1}>{doc.title}</Text>
                        <Text size="xs" c="dimmed" lineClamp={1}>{doc.original_filename}</Text>
                      </div>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" variant="light">{doc.file_type.toUpperCase()}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{formatFileSize(doc.file_size)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{doc.chunks_count}</Text>
                  </Table.Td>
                  <Table.Td>
                    {getStatusBadge(doc.status)}
                    {doc.status === 'error' && doc.error_message && (
                      <Tooltip label={doc.error_message} multiline w={300}>
                        <IconAlertTriangle size={14} color="var(--mantine-color-red-5)" style={{ marginLeft: 4, cursor: 'help' }} />
                      </Tooltip>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {new Date(doc.created_at).toLocaleDateString()}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Menu shadow="md" width={180} position="bottom-end">
                      <Menu.Target>
                        <ActionIcon variant="subtle" color="gray">
                          <IconDotsVertical size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          leftSection={<IconEye size={14} />}
                          onClick={() => handleViewDetail(doc)}
                        >
                          {t('aiKnowledge.documents.viewDetails')}
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconRefresh size={14} />}
                          onClick={() => handleReindex(doc)}
                        >
                          {t('aiKnowledge.documents.reindex')}
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item
                          leftSection={<IconTrash size={14} />}
                          color="red"
                          onClick={() => handleDelete(doc)}
                        >
                          {t('common.delete')}
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Group justify="center">
          <Pagination
            value={page}
            onChange={setPage}
            total={totalPages}
          />
        </Group>
      )}

      {/* Upload Modal */}
      <UploadDocumentModal
        opened={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onSuccess={() => {
          setUploadModalOpen(false);
          fetchDocuments();
          onDataChange();
        }}
      />

      {/* Detail Modal */}
      <Modal
        opened={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        title={detailDoc?.title || t('aiKnowledge.documents.viewDetails')}
        size="lg"
      >
        {detailLoading ? (
          <Group justify="center" py="xl"><Loader /></Group>
        ) : detailDoc ? (
          <Stack gap="md">
            <Group gap="lg">
              <div>
                <Text size="xs" c="dimmed">{t('aiKnowledge.documents.columns.type')}</Text>
                <Badge size="lg" variant="light">{detailDoc.file_type.toUpperCase()}</Badge>
              </div>
              <div>
                <Text size="xs" c="dimmed">{t('aiKnowledge.documents.columns.size')}</Text>
                <Text fw={500}>{formatFileSize(detailDoc.file_size)}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">{t('aiKnowledge.documents.columns.chunks')}</Text>
                <Text fw={500}>{detailDoc.chunks_count}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">{t('aiKnowledge.documents.columns.status')}</Text>
                {getStatusBadge(detailDoc.status)}
              </div>
            </Group>

            {detailDoc.error_message && (
              <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />}>
                {detailDoc.error_message}
              </Alert>
            )}

            {detailDoc.extracted_text && (
              <div>
                <Text size="sm" fw={600} mb="xs">{t('aiKnowledge.documents.extractedText')}</Text>
                <Paper
                  p="md"
                  radius="md"
                  withBorder
                  style={{
                    maxHeight: 400,
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    fontSize: '13px',
                    fontFamily: 'monospace',
                    background: colorScheme === 'dark' ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-gray-0)'
                  }}
                >
                  {detailDoc.extracted_text.substring(0, 5000)}
                  {detailDoc.extracted_text.length > 5000 && '\n\n... (truncated)'}
                </Paper>
              </div>
            )}
          </Stack>
        ) : null}
      </Modal>
    </Stack>
  );
};

export default DocumentsTab;
